import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Card, CardHeader, CardContent } from '@material-ui/core';
import { Loader, Button, colors } from 'kangarootime-ui';
import moment from 'moment';
import DateInput from './DateInput';
import TimeInput from './TimeInput';
import BreakMinutesInput from './BreakMinutesInput';
import RoomSelectionInput from './RoomSelectionInput';
import EmployeeSelection from './EmployeeSelection';
import ktRequest from '../../../utils/request';

const ScheduleModal = ({
  isOpen,
  onClose,
  date,
  createSchedule,
  performingAction,
  shift,
  editShift,
  selectedRoomInformation,
  employeesToDisableSelection
}) => {
  const MINIMUM_BREAK_LENGTH_MINUTES = 30;
  const editingExistingShift = shift !== null;
  /**
   * React hooks
   */
  const [startTime, setStartTime] = editingExistingShift ? useState(moment(shift.start)) : useState(moment(date).hours(9).minutes(0).second(0).millisecond(0));
  const [endTime, setEndTime] = editingExistingShift ? useState(moment(shift.end)) : useState(moment(date).hours(17).minutes(0).second(0).millisecond(0));
  const [breakMinutes, setBreakMinutes] = editingExistingShift ? useState(parseInt(shift.breakMinutes, 10)) : useState(MINIMUM_BREAK_LENGTH_MINUTES);
  const [validTimeframe, setValidTimeframe] = editingExistingShift ? useState(moment(shift.start).isBefore(moment(shift.end))) : useState(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(editingExistingShift ? shift.employeeId : null);
  // when set `selectedEmployeeBlocker` will be an object with properties `hasBlocker` and `message`
  const [selectedEmployeeBlocker, setSelectedEmployeeBlocker] = useState(null);

  const validBreakMinutesAmount = breakMinutes >= 0;
  /**
   * ensure that the selected timeframe is valid
   * - start time is before end time / end time is after start time
   * - start time is after "now" if selected date is today
   */
  useEffect(() => {
    if (
      (startTime.isAfter(endTime) && validTimeframe) ||
      (endTime.isBefore(startTime) && validTimeframe)
    ) {
      setValidTimeframe(false);
    } else if (
      (startTime.isBefore(endTime) && !validTimeframe) ||
      (endTime.isAfter(startTime) && !validTimeframe)
    ) {
      setValidTimeframe(true);
    }
  }, [startTime, endTime]);

  // check to see if the selected employee has any blockers on the selected date
  useEffect(() => {
    (async () => {
      try {
        if (selectedEmployeeId) {
          const date = moment(startTime).format('YYYY-MM-DD');
          const response = await ktRequest.get(`scheduling/schedule/check-shift-blocker?employeeId=${selectedEmployeeId}&date=${date}`, 'timecards');
          setSelectedEmployeeBlocker(response);
        }
      } catch (error) {
        setSelectedEmployeeBlocker(null);
      }
    })();
  }, [selectedEmployeeId]);

  /**
   * generate an array of moment objects in 15 minute increments
   */
  const generateTimes = startDate => {
    let times = [];
    // limit time input options from 6am-9pm
    let start = moment(startDate).startOf('day').hours(6);
    const end = moment(startDate).startOf('day').hours(21).minute(15);

    while (start < end) {
      const _start = start;
      times.push(_start.clone());
      start = _start.add('m', 15);
    }

    return times;
  };

  const handleSubmit = () => {
    editingExistingShift ? editShift(selectedEmployeeId, selectedRoomInformation.id, startTime, endTime, breakMinutes) : createSchedule(selectedEmployeeId, selectedRoomInformation.id, startTime, endTime, breakMinutes);
    handleClose();
  };

  const handleOutsideClick = event => {
    const { target } = event;

    // if a click is registered to the left or right outside of the card, we need to dismiss the modal
    if (target.id && target.id === 'outside-schedule-modal') {
      handleClose();
    }
  };

  const handleClose = () => {
    // reset state before closing
    setStartTime(moment(date).hours(9).minutes(0).second(0).millisecond(0));
    setEndTime(moment(date).hours(17).minutes(0).second(0).millisecond(0));
    setBreakMinutes(MINIMUM_BREAK_LENGTH_MINUTES);
    setValidTimeframe(true);
    setSelectedEmployeeId(null);
    setSelectedEmployeeBlocker(null);

    onClose && onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <div id='outside-schedule-modal' className='flex' style={{ justifyContent: 'center', alignItems: 'center', marginTop: 32 }} onClick={handleOutsideClick}>
        <Card style={{ width: 540, overflow: 'visible' }}>
          <CardHeader style={{ backgroundColor: colors.brand_1, color: '#FFFFFF' }} title={editingExistingShift ? 'Edit Shift' : 'Create Shift'} />
          <CardContent>
            { selectedEmployeeBlocker && selectedEmployeeBlocker.hasBlocker &&
              <div
                className='kt-alert'
                style={{ width: '100%', margin: '8px 0', '--kt-alert-color': colors.info }}
                role='alert'
              >
                <span className='kt-alert-title'>Notice</span> {selectedEmployeeBlocker.message}
              </div>
            }
            <EmployeeSelection
              allowEmployeeSelection={!editingExistingShift}
              selectedEmployeeId={selectedEmployeeId}
              onSelect={employee => setSelectedEmployeeId(employee.id)}
              employeesToDisableSelection={editingExistingShift ? {} : employeesToDisableSelection}
            />
            <div className='flex-rows'>
              <DateInput date={date} />
              <div className='flex-1' style={{ marginLeft: 8 }}>
                <RoomSelectionInput selectedRoom={selectedRoomInformation.name} />
              </div>
            </div>
            <div className='flex-rows'>
              <TimeInput
                label='Time Start'
                time={startTime}
                options={generateTimes(date)}
                handleSelection={time => setStartTime(time)}
              />
              <div style={{ marginLeft: 8 }}>
                <TimeInput
                  label='Time End'
                  time={endTime}
                  options={generateTimes(date)}
                  handleSelection={time => setEndTime(time)}
                />
              </div>
              <div className='flex-1' style={{ marginLeft: 8 }}>
                <BreakMinutesInput breakMinutes={breakMinutes} setBreakMinutes={setBreakMinutes} />
              </div>
            </div>
            <div className='flex-columns'>
              { !validTimeframe && <label style={{ color: colors.danger, fontFamily: 'proxima-soft, sans-seriff' }}>Invalid timeframe</label> }
            </div>
            <Loader loading={performingAction}>
              <div className='flex-columns' style={{ marginTop: 16, alignItems: 'center' }}>
                <Button
                  onClick={handleSubmit}
                  disabled={!validTimeframe || !validBreakMinutesAmount || selectedEmployeeId === null}
                >Submit</Button>
                <div onClick={handleClose} style={{ marginTop: 16 }}>
                  <label style={{ color: '#6B778C', fontFamily: 'proxima-soft, sans-seriff' }}>Cancel</label>
                </div>
              </div>
            </Loader>
          </CardContent>
        </Card>
      </div>
    </Modal>
  );
};

ScheduleModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  date: PropTypes.oneOf(PropTypes.instanceOf(Date), PropTypes.string),
  createSchedule: PropTypes.func,
  performingAction: PropTypes.bool,
  shift: PropTypes.shape({
    id: PropTypes.number,
    roomId: PropTypes.number,
    employeeId: PropTypes.number,
    start: PropTypes.string,
    end: PropTypes.string
  }),
  editShift: PropTypes.func,
  selectedRoomInformation: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  }),
  employeesToDisableSelection: PropTypes.shape({
    [PropTypes.number]: PropTypes.any
  })
};

export default ScheduleModal;
